// TODO: Switch to controller approach
const importObservers = new Map()
const deferredImports = {}
const importedScripts = []

// TODO: Add support for "import on scroll"
class DeferredImport {
  #assets
  #element
  #options
  observer

  constructor(assets, element, options = {}) {
    if (!assets) {
      throw new Error(
        'DeferredImport initialization failed: No assets provided.'
      )
    }

    if (!element && !options.loadOnIdle) {
      throw new Error('DeferredImport error: No element provided.')
    }

    console.log('CONSTRUCTING DEFERRED IMPORT', assets)

    this.assets = assets
    this.element = element
    this.options = options
    // this.intersectionObserver = null

    this.idleCallback = this.loadComponentOnIdle.bind(this)

    if (this.element) {
      lowPriority(this.checkInitialPosition.bind(this), 1)
    }
  }

  get assets() {
    return this.#assets
  }

  set assets(value) {
    this.#assets = Array.isArray(value) ? value : [value]
    // this.#assets = (Array.isArray(value) ? value : [value]).map((asset) => {
    //   if (!asset.includes('/')) asset = `${defaultType}/${asset}`

    //   return asset.split('/')
    // })
  }

  get key() {
    return JSON.stringify(this.#assets)
  }

  get element() {
    let element = this.#element

    if (typeof element === 'string') {
      element = document.querySelector(element)
    } else if (typeof element === 'function') {
      element = value()
    }

    if (!element && !this.loadOnIdle) {
      throw new Error('DeferredImport error: Element not found.')
    }

    return element
  }

  set element(value) {
    this.#element = value
  }

  get options() {
    return this.#options
  }

  set options({
    defaultType = 'component',
    loadOnIdle = false,
    offset = '200px',
  } = {}) {
    offset =
      typeof offset === 'number' || !isNaN(Number(offset))
        ? `${offset}px`
        : offset

    this.#options = { defaultType, loadOnIdle, offset }
  }

  get loadOnIdle() {
    return this.#options.loadOnIdle
  }

  initObserver() {
    if (!this.element) return

    const observeComponent = this.observeComponent.bind(this)
    this.observer = new IntersectionObserver(observeComponent, {
      rootMargin: `0px 0px ${this.options.offset} 0px`,
    })
    this.observer.observe(this.element)

    // if (!importObservers.has(observerKey)) importObservers.set(observerKey, [])
    // importObservers.get(observerKey).push(observer)
  }

  init() {
    if (this.key in deferredImports) {
      console.debug(
        'DeferredImport already initialized with an earlier element target. Skipping initialization.',
        this.element,
        'Existing element target:',
        deferredImports[this.key].element
      )
      return
    }

    console.debug(
      'Initializing DeferredImport',
      this.assets,
      this.element,
      this.options
    )

    if (this.options.instant) {
      this.loadComponent()
    } else {
      this.initObserver()
      this.idleListener()
    }

    deferredImports[this.key] = this
  }

  destroy() {
    this.observer?.disconnect()
    this.idleListener(false)
    delete deferredImports[this.key]

    if (this.element && this.element.hasAttribute('data-intersection-import')) {
      this.element.remove()
    }
  }

  idleListener(enable = true) {
    if (!this.loadOnIdle) return

    const method = enable ? 'addEventListener' : 'removeEventListener'
    window[method]('load', this.idleCallback)
  }

  checkInitialPosition() {
    const bounds = this.element.getBoundingClientRect()
    if (bounds.top <= 0) {
      this.loadComponent()
    }
  }

  async loadComponent() {
    for (const asset of this.assets) {
      if (importedScripts.includes(asset)) continue

      try {
        await import(asset)
        importedScripts.push(asset)
        console.debug('script imported:', `${asset}`)
      } catch (error) {
        console.error('DeferredImport error: Error importing script:', asset, error)
      }
    }

    this.destroy()

    // Remove observers loading the same assets
    // const observersWithKey = importObservers.get(this.key)
    // if (observersWithKey) {
    //   observersWithKey.forEach((observer) => observer.disconnect())
    //   importObservers.delete(this.key)
    // }
  }

  async loadComponentOnIdle() {
    const callback = async () => {
      console.debug('importing scripts on idle:', this.key)
      await this.loadComponent()
    }

    afterIdle(callback)
    // requestIdleCallback()
  }

  async observeComponent(entries) {
    entries.forEach(async (entry) => {
      if (entry.isIntersecting) {
        console.debug('importing scripts on intersection:', this.key)
        await this.loadComponent()
      }
    })
  }
}

/**
 * Dynamically imports assets and loads them a specified element approaches intersection with the viewport.
 *
 * @param {string[]|string} assets - The assets to be imported. If a single string is provided, it is assumed to be a selector for the element to observe.
 * @param {string|HTMLElement} element - The element to observe for intersection. If a string is provided, it is assumed to be a selector for the element.
 * @param {Object} options - Optional parameters for customizing the behavior.
 * @param {boolean} [options.loadOnIdle=false] - Whether to load the assets on idle time.
 * @param {string} [options.offset='200px'] - The offset for the intersection observer.
 * @param {boolean} [options.instant=false] - Trigger import instantly (primarily used for tests).
 * @returns {void}
 *
 * @example
 * importOnIntersection(['asset1', 'asset2'], document.querySelector('.my-element'))
 */
export const importOnIntersection = async (
  assets,
  element,
  {
    defaultType = 'component',
    loadOnIdle = false,
    offset = '200px',
    instant = false,
  } = {}
) => {
  const deferredImport = new DeferredImport(assets, element, {
    defaultType,
    loadOnIdle,
    offset,
    instant,
  })
  deferredImport.init()
}

export const triggerDeferredImports = async () => {
  console.warn(
    'Manual load of all deferred script imports has been triggered. Importing assets:',
    deferredImports
  )
  const imports = Object.values(deferredImports)

  await Promise.all(
    imports.map(async (deferredImport) => await deferredImport.loadComponent())
  )
}

export const afterIdle = (callback, secondsDelay = 2) => {
  setTimeout(requestIdleCallback.bind(null, callback), secondsDelay * 1000)
}

/**
 * Runs the provided callback function after a specified delay on idle time after the window load event is complete.
 *
 * @param {Function} callback - The function to be executed after the specified delay.
 * @param {number} [secondsDelay=1] - The delay in seconds before executing the callback function. Default is 1 second.
 *
 * @returns {void}
 *
 * @example
 * lowPriority(() => {
 *   console.log('Callback executed 2 seconds after idle.');
 * }, 2);
 */
export const lowPriority = (callback, secondsDelay = 2) => {
  // const idleCallback = setTimeout.bind(
  //   null,
  //   requestIdleCallback.bind(null, callback),
  //   secondsDelay * 1000
  // )
  // const idleCallback = requestIdleCallback.bind(
  //   null,
  //   setTimeout.bind(null, callback, secondsDelay * 1000)
  // )

  const idleCallback = afterIdle.bind(null, callback, secondsDelay)

  if (document.readyState === 'complete') {
    idleCallback()
  } else {
    window.addEventListener('load', idleCallback)
  }
}
